import { SearchFigma } from '@components/SearchBars';
import Text from '@components/Text/Text';
import { useHookstate } from '@hookstate/core';
import { specDataUpdatedAt } from '@pages/Asset/Asset.query';
import { setAttr } from '@utils/setAttr';
import { memo, useState } from 'react';
import { useQuery } from 'react-query';
import styled from 'styled-components';
import ProductSpec from './ProductSpec';
import ProductSpecGroup, { matchesGroup } from './ProductSpecGroup';
import { ProductSpecsKey, productSpecsQuery } from './ProductSpecs.query';

interface ProductSpecsBodyProps {
  // Divprops
  className?: string | undefined;
  productId: string | undefined | null;
}
type ProductSpecsBodyType = React.FC<ProductSpecsBodyProps>;
function ProductSpecsBody(props: ProductSpecsBodyProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const updatedAt = useHookstate(specDataUpdatedAt);
  const { productId, ...rest } = props;

  function startSearch(text = '') {
    setSearchTerm(text);
    const now = new Date().toJSON();
    updatedAt.set(now);
  }
  function onClear() {
    startSearch();
  }

  const qKey = productSpecsQuery.queryKey as ProductSpecsKey;

  const { data, isLoading, isSuccess } = useQuery({
    ...productSpecsQuery,
    queryKey: [qKey[0], qKey[1], productId, searchTerm, updatedAt.get()],
  });

  const { total } = data || {};
  const resultsArr = data?.results ?? [];
  const isSearch = searchTerm.length > 0;

  // Assumption: Either we have all groups, or all without groups on the product.
  const withGroups = resultsArr.filter(each => {
    return !!each.specGroupName;
  });
  const withoutGroups = resultsArr.filter(each => {
    return !each.specGroupName;
  });
  const hasNoSpecs = resultsArr.length === 0;

  const showNoSpecs = !isSearch && hasNoSpecs && isSuccess;
  const showResultsFound = isSearch && isSuccess;
  // todo: Note error.
  // const errText = isError && 'Error fetching specs';

  return (
    <div {...rest}>
      <div className="SpecSearchContainer">
        <SearchFigma
          startSearch={startSearch}
          onClear={onClear}
          loading={isLoading}
        />
        <Text text="h200" textColor="n600" hidden={setAttr(!showResultsFound)}>
          {total}{' '}
          <Text inherit text="p100" span>
            Results found
          </Text>
        </Text>
        <Text hidden={setAttr(!showNoSpecs)} textColor="n600" text="p100">
          No Specs are available for this product.
        </Text>
      </div>
      <div hidden={setAttr(withoutGroups.length === 0)}>
        {withoutGroups.map(entry => {
          return entry.specNames.map(spec => {
            return (
              <ProductSpec
                key={spec.id}
                specName={spec.specName}
                specDetail={spec.specDetail}
                searchTerm={searchTerm}
              />
            );
          });
        })}
      </div>
      {withGroups.map(specValue => {
        const { specNames, id } = specValue;
        let groupResults: number | undefined;
        if (matchesGroup(searchTerm)) {
          groupResults = specNames.length;
        }
        return (
          <ProductSpecGroup
            name={specValue.specGroupName}
            searchTerm={searchTerm}
            key={id}
            results={groupResults}
            specs={specNames}
          />
        );
      })}
    </div>
  );
}

export default memo(styled(ProductSpecsBody)`
  display: flex;
  flex-direction: column;
  row-gap: 24px;

  .SpecSearchContainer {
    display: flex;
    flex-direction: column;
    row-gap: 8px;
  }
  .GroupHead,
  .SpecHead {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
  }
`) as ProductSpecsBodyType;
